import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { SchoolsService } from '../../_services/schools.service';
import {
  Firestore,
  collection,
  query,
  where,
  doc,
  setDoc,
  deleteDoc,
  collectionData,
  CollectionReference,
  docData,
} from '@angular/fire/firestore';
import { UntypedFormGroup, UntypedFormControl, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Observable, firstValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { User } from '../../_models/user';
import { Session } from '../../_models/result';
import { convertTimestampsPipe } from '../../_pipes/convert-firestore-timestamp.pipe';
import { MatButton } from '@angular/material/button';
import { AsyncPipe, JsonPipe } from '@angular/common';
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent, MatCardSubtitle } from '@angular/material/card';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatChip } from '@angular/material/chips';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-superadmin',
  templateUrl: './superadmin.component.html',
  styleUrls: ['./superadmin.component.css'],
  imports: [
    MatButton,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatCardSubtitle,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    AsyncPipe,
    JsonPipe,
    MatChip,
    MatTooltip,
    DatePipe,
  ],
})
export class SuperadminComponent implements OnInit {
  schoolId = '';
  activeUsers?: Observable<User[]>;
  pendingEplUsers: Observable<User[]>;

  schoolSettingsForm: UntypedFormGroup;
  schoolSettingsLoading = true;
  schoolSettingsSaving = false;
  schoolSettingsError: string | null = null;
  private savedStatus = '';
  private savedInstitutionalMethod = false;

  calcRulesPath = '';

  eclUserSessions: Observable<Session[]> | null = null;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    public sa: SchoolsService,
    private firestore: Firestore,
    private fb: UntypedFormBuilder,
  ) {
    this.titleService.setTitle('Super Admin' + ' | NPC Admin');

    this.schoolSettingsForm = this.fb.group({
      status: [''],
      live: [''],
      schoolProperties: this.fb.group({
        orgId: new UntypedFormControl(null),
        institutionalMethod: new UntypedFormControl(false),
      }),
    });

    const pendingCol = collection(this.firestore, 'admin_users') as CollectionReference<User>;
    const pendingQuery = query(pendingCol, where('admin', '==', false), where('eplUserId', '>', ''));
    this.pendingEplUsers = (collectionData(pendingQuery) as Observable<User[]>).pipe(
      convertTimestampsPipe(),
      map(users => users.filter(user => !user.schools?.length)),
    );
  }

  ngOnInit() {
    this.schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
    const activeUsersCol = collection(this.firestore, 'admin_users') as CollectionReference<User>;
    const activeQuery = query(
      activeUsersCol,
      where('schools', 'array-contains', this.schoolId),
      where('admin', '==', false),
    );
    this.activeUsers = (collectionData(activeQuery) as Observable<User[]>).pipe(convertTimestampsPipe());

    this.calcRulesPath = `calcConfig/${this.schoolId}/calcRules/draft`;
    void this.loadSchoolSettings();
  }

  hasEpl(user: User): boolean {
    return !!user.eplUserId?.trim();
  }

  lastLoginDate(user: User): Date | null {
    const value = user.lastLoginAt;
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
      return (value as { toDate: () => Date }).toDate();
    }
    return null;
  }

  rowHeaderId(user: User): string {
    return `permission-user-${user.uid}`;
  }

  async loadSchoolSettings(): Promise<void> {
    this.schoolSettingsLoading = true;
    this.schoolSettingsError = null;

    try {
      const prefsRef = doc(this.firestore, 'preferences', this.schoolId);
      const calcRulesRef = doc(this.firestore, this.calcRulesPath);
      const schoolRef = doc(this.firestore, 'schools', this.schoolId);

      const [prefs, calcRules, school] = await Promise.all([
        firstValueFrom(docData(prefsRef).pipe(take(1))),
        firstValueFrom(docData(calcRulesRef).pipe(take(1))),
        firstValueFrom(docData(schoolRef).pipe(take(1), convertTimestampsPipe())),
      ]);

      const status = (prefs?.['status'] as string | null | undefined) ?? '';
      const schoolProperties = (calcRules?.['schoolProperties'] as Record<string, unknown> | undefined) ?? {};
      const institutionalMethod = (schoolProperties['institutionalMethod'] as boolean | undefined) ?? false;

      this.savedStatus = status;
      this.savedInstitutionalMethod = institutionalMethod;
      this.schoolSettingsForm.patchValue({
        status,
        live: this.toDateInputValue(school?.['live']),
        schoolProperties: {
          orgId: schoolProperties['orgId'] ?? null,
          institutionalMethod,
        },
      });
      this.schoolSettingsForm.markAsPristine();
    } catch (err) {
      console.error('Failed to load school settings', err);
      this.schoolSettingsError = 'Unable to load school settings.';
    } finally {
      this.schoolSettingsLoading = false;
    }
  }

  async saveSchoolSettings(): Promise<void> {
    if (!this.schoolSettingsForm.valid || this.schoolSettingsForm.pristine) {
      return;
    }

    this.schoolSettingsSaving = true;
    this.schoolSettingsError = null;

    const status = this.schoolSettingsForm.controls.status.value as string;
    const schoolProperties = this.schoolSettingsForm.get('schoolProperties')?.getRawValue();
    const institutionalMethod = schoolProperties.institutionalMethod as boolean;

    try {
      await setDoc(
        doc(this.firestore, this.calcRulesPath),
        { schoolProperties },
        { merge: true },
      );
      await setDoc(
        doc(this.firestore, 'preferences', this.schoolId),
        { status: status || null },
        { merge: true },
      );

      if (status !== this.savedStatus) {
        await this.applySchoolStatus(status);
        this.savedStatus = status;
      }

      if (this.schoolSettingsForm.controls.live.dirty) {
        const liveValue = this.schoolSettingsForm.controls.live.value as string;
        const live = liveValue ? new Date(`${liveValue}T00:00:00`) : null;
        await setDoc(doc(this.firestore, 'schools', this.schoolId), { live }, { merge: true });
      }

      await this.sa.updateSchool(this.schoolId, { schoolProperties });
      this.savedInstitutionalMethod = institutionalMethod;
      this.schoolSettingsForm.markAsPristine();
    } catch (err) {
      console.error('Failed to save school settings', err);
      this.schoolSettingsError = 'Unable to save school settings.';
    } finally {
      this.schoolSettingsSaving = false;
    }
  }

  private async applySchoolStatus(status: string): Promise<void> {
    const schoolDocRef = doc(this.firestore, 'schools', this.schoolId);
    const prefsDocRef = doc(this.firestore, 'preferences', this.schoolId);
    const draftPrefsDocRef = doc(this.firestore, 'preferences_draft', this.schoolId);

    if (status === '') {
      await setDoc(schoolDocRef, { disabledDate: null, status: null, live: new Date(Date.now()) }, { merge: true });
      await setDoc(prefsDocRef, { disableSchool: false }, { merge: true });
      await setDoc(draftPrefsDocRef, { disableSchool: false }, { merge: true });
    } else {
      await setDoc(schoolDocRef, { disabledDate: new Date(Date.now()), status }, { merge: true });
      await setDoc(prefsDocRef, { disableSchool: true }, { merge: true });
      await setDoc(draftPrefsDocRef, { disableSchool: true }, { merge: true });
    }
    await setDoc(draftPrefsDocRef, { status: null }, { merge: true });
  }

  private toDateInputValue(value: unknown): string {
    if (!value) {
      return '';
    }
    let date: Date;
    if (value instanceof Date) {
      date = value;
    } else if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
      date = (value as { toDate: () => Date }).toDate();
    } else {
      date = new Date(value as string);
    }
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().slice(0, 10);
  }

  getEclUserRecords(eclUserId: string) {
    this.eclUserSessions = null;
    if (eclUserId.toUpperCase() !== 'GUEST') {
      const sessionCol = collection(this.firestore, 'sessions') as CollectionReference<Session>;
      const q = query(sessionCol, where('cbUserId', '==', eclUserId.toUpperCase()));
      this.eclUserSessions = collectionData(q, { idField: 'id' }) as Observable<Session[]>;
    }
  }

  deleteUserSessions(sessions: Session[]) {
    const confirmation = prompt('Type yes to confirm deletion of ' + sessions.length + ' records');

    if (confirmation.toLowerCase() === 'yes') {
      sessions.forEach(session => {
        const docRef = doc(this.firestore, 'sessions', session.id);
        deleteDoc(docRef);
      });
    }
  }

  deactivate(user: User) {
    const index = user.schools.indexOf(this.schoolId);
    if (index !== -1) {
      user.schools.splice(index, 1);
      user.schools = user.schools.length === 0 ? null : user.schools;
    } else {
      console.log('Can not find', this.schoolId, 'when attempting to deactivate user.schools = ', user.schools);
    }
    const docRef = doc(this.firestore, 'admin_users', user.uid);
    setDoc(docRef, user);
  }

  activate(user: User) {
    if (user.schools === null) {
      user.schools = [];
    }
    user.schools.push(this.schoolId);
    const docRef = doc(this.firestore, 'admin_users', user.uid);
    setDoc(docRef, user);
  }

  deleteUser(user: User) {
    const confirmation = prompt('Type yes to confirm deletion of ' + user.email);

    if (confirmation.toLowerCase() === 'yes') {
      const docRef = doc(this.firestore, 'admin_users', user.uid);
      deleteDoc(docRef);
    }
  }

}
